import {
  sampleProduct2,
  sampleProducts,
} from "../../dummyData/SampleProductList";
import {
  CreateProductModalState,
  NewProductModal,
  ProductDetailsModal,
  ListingTypeVariantConfig,
} from "../../interfaces/ProductObject";
import { UserPreferences } from "../../interfaces/UserObject";
import ProductConvertor, {
  BulkProductContext,
} from "../converters/uiToBackend/ProductConvertor";
import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { getPublicAsset, getRequest, postRequest, putRequest } from "../http/httpRequest";
import { conventionalProductImageUrl } from "../helpers/ProductImageHelper";
import genericUtility from "../utility/GenericUtility";
import { throwIfNotOk, throwIfErrorInBody } from "../helpers/ApiErrorHelper";

async function createProduct(
  product: CreateProductModalState,
  locationOverride?: { latitude: number; longitude: number } | null,
  variantsConfig?: ListingTypeVariantConfig | null
): Promise<any> {
  const endpoint = "/api/v1/products/createProduct";
  // When a location override is supplied (e.g. the attached business's
  // coordinates), use it and skip the GPS lookup entirely. Otherwise fall back
  // to the existing current-location flow.
  const currentUserLocation = locationOverride ?? (await genericUtility.getCurrentUserLocation());
  const payload = ProductConvertor.buildCreateProductPayload(product, currentUserLocation, variantsConfig);
  const response = await postRequest(endpoint, payload, true);
  // A non-2xx here (502/503/504 with an HTML/empty body, an auth failure,
  // etc.) has no usable JSON -- parse it first and response.json() throws a
  // cryptic SyntaxError instead of real copy.
  await throwIfNotOk(response, "Couldn't create the product. Please try again.");
  const data = await response.json();
  // A VALIDATION_ERROR from this controller comes back as HTTP 200 with
  // errorInformation populated (see throwIfErrorInBody's doc comment), so
  // throwIfNotOk above can't catch it -- this checks the body itself.
  throwIfErrorInBody(data, "Couldn't create the product. Please try again.");
  return data;
}

/**
 * Creates many products in one call, from a pasted list of create-product
 * payloads. Used by the curator's bulk-upload modal on an in-progress Nine Setup
 * row (see NineSetupBulkActions).
 *
 * Deliberately NOT a loop over createProduct(): that would fire N requests, half
 * of which can succeed, leaving a page in a state no one asked for. One request
 * means the backend owns atomicity — and it also skips N GPS lookups, since the
 * business's own coordinates are stamped on every item by the converter.
 *
 * Throws (via throwIfNotOk) on a non-2xx so the caller can surface real copy
 * rather than reading a failure as an empty success.
 */
async function bulkCreateProducts(
  items: unknown[],
  context: BulkProductContext
): Promise<any> {
  const endpoint = "/api/v1/products/bulkCreateProduct";
  const payload = ProductConvertor.buildBulkCreateProductsPayload(
    items,
    context
  );
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response, "Couldn't create those products. Please try again.");
  return response.json().catch(() => null);
}

/**
 * Deletes many products by id. Used by the curator's "delete all products"
 * action on an in-progress Nine Setup row (see NineSetupBulkActions).
 *
 * There is no bulk-delete endpoint, so this fans out over the single-product
 * one. Three deliberate choices:
 *
 * 1. It does NOT call `deleteProduct` above, which returns `response.json()`
 *    without checking `response.ok` — a 403 or 500 resolves there and would be
 *    counted as a success. This issues the same request and runs it through
 *    throwIfNotOk so a failure is actually a failure.
 * 2. Batched at DELETE_CONCURRENCY rather than one Promise.all over everything:
 *    "delete all" on a 200-product page would otherwise open 200 sockets at
 *    once and invite a rate limit that turns a clean wipe into a partial one.
 * 3. allSettled, not all — the caller needs the split. A partial delete is a
 *    real outcome the UI has to report honestly, and aborting the remaining
 *    batches on the first failure would only make it more partial.
 *
 * Never throws for an individual failure; the returned `failed` list is the
 * error channel. A total network outage still rejects, via the first batch.
 */
const DELETE_CONCURRENCY = 5;

async function deleteProductsByIds(
  productIds: string[]
): Promise<{ deleted: string[]; failed: string[] }> {
  const deleted: string[] = [];
  const failed: string[] = [];

  for (let start = 0; start < productIds.length; start += DELETE_CONCURRENCY) {
    const batch = productIds.slice(start, start + DELETE_CONCURRENCY);

    const results = await Promise.allSettled(
      batch.map(async (productId) => {
        const endpoint = `/api/v1/products/delete-product?pid=${productId}`;
        const response = await postRequest(endpoint, {}, true);
        await throwIfNotOk(response, "Couldn't delete that product.");
      })
    );

    results.forEach((result, index) => {
      if (result.status === "fulfilled") deleted.push(batch[index]);
      else failed.push(batch[index]);
    });
  }

  return { deleted, failed };
}

/**
 * Hard-deletes many products at once via the bulk delete endpoint
 * (POST /api/v1/products/delete-products-by-ids), scoped to a single
 * business. Used by the "delete all products in this section / sub-section"
 * action on the business Products tab.
 *
 * Unlike deleteProductsByIds above -- which fans out over the single-product
 * delete endpoint for the curator's "delete all products on this page" Nine
 * Setup action, because no bulk endpoint existed yet -- this calls the real
 * bulk endpoint directly: one atomic, all-or-nothing request. The backend
 * requires every id to belong to businessId, or NONE of them are deleted, so
 * there is no partial-success case to report here (contrast with
 * deleteProductsByIds' { deleted, failed } split).
 *
 * Curator-gated server-side (the caller must be a curator AND a collaborator
 * on businessId) -- throwIfNotOk surfaces a denial as a friendly error via
 * extractApiErrorMessage rather than a raw 403.
 */
async function deleteProductsByBusinessId(
  businessId: string,
  productIds: string[]
): Promise<void> {
  const endpoint = "/api/v1/products/delete-products-by-ids";
  const payload = { businessId, productIds };
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response, "Couldn't delete those products. Please try again.");
}

async function updateProduct(
  product: CreateProductModalState,
  updateLocation: boolean = false,
  variantsConfig?: ListingTypeVariantConfig | null
): Promise<any> {
  const endpoint = `${ServiceEndpoints.UPDATE_PRODUCT}/${product.productId}`;
  
  let currentUserLocation = null;
  if (updateLocation) {
    currentUserLocation = await genericUtility.getCurrentUserLocation();
  }

  const payload = ProductConvertor.buildCreateProductPayload(
    product,
    currentUserLocation,
    variantsConfig
  );
  const includeBearerToken = true;
  const response = await putRequest(endpoint, payload.product, includeBearerToken);
  await throwIfNotOk(response, "Couldn't update the product. Please try again.");
  const data = await response.json();
  // Same always-200-on-body-error behavior as createProduct -- see
  // throwIfErrorInBody's doc comment.
  throwIfErrorInBody(data, "Couldn't update the product. Please try again.");
  return data;
}

/**
 * Saves a server-built partial UpdateProductRq as-is — the `result` / `undo`
 * payloads of a voice interpretation (see interfaces/VoiceObject.ts). Same
 * endpoint and the same error handling as updateProduct above (including the
 * always-200-on-body-error case), but no conversion from the editor's
 * CreateProductModalState: the server already built exactly the fields to
 * change, and null fields are left untouched by update-product.
 */
async function applyProductPatch(productId: string, patch: unknown): Promise<void> {
  const endpoint = `${ServiceEndpoints.UPDATE_PRODUCT}/${productId}`;
  const includeBearerToken = true;
  const response = await putRequest(endpoint, patch, includeBearerToken);
  await throwIfNotOk(response, "Couldn't save the change. Please try again.");
  const data = await response.json().catch(() => null);
  throwIfErrorInBody(data, "Couldn't save the change. Please try again.");
}

async function getProductsByIds(
  productIds: string[],
  options?: {
    /** Attach infoContent + sponsors to Info listings. Only the product
     *  details page sets this -- cart, dine-in and feeds leave it off so their
     *  payloads stay unchanged. */
    includeInfoContent?: boolean;
  }
): Promise<ProductDetailsModal[]> {
  const endpoint = "/api/v1/products/getProductsByIds";
  const payload = {
    productIds,
    includeQueries: true,
    includeImages: false,
    // Only sent when asked for, so every other caller's payload stays byte-identical.
    ...(options?.includeInfoContent === true ? { includeInfoContent: true } : {}),
  };
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

async function getProductDetailsById(
  productId: string
): Promise<ProductDetailsModal> {
  const endpoint = `/api/v1/products/${productId}/details`;
  const includeBearerToken = false; // Public endpoint - no authentication required
  const response = await getRequest(endpoint, includeBearerToken);
  await throwIfNotOk(response);
  const data = await response.json();
  // Wrapped the same way every other products/* endpoint wraps a single
  // result — NOT the bare product the return type implies. Unwrapped here,
  // once, so every caller can actually trust the declared type instead of
  // each one needing to know (or forgetting, as onProductUpdate in both
  // BusinessListings.tsx and MyListings.tsx did) to reach into .contents[0].
  const product = data?.contents?.[0];
  if (!product) throw new Error("Product not found");
  return product;
}

async function getProductsByUserIds(
  userId: string
): Promise<ProductDetailsModal[]> {
  const endpoint = "/api/v1/products/getProductsByUserId";
  const payload = { userId, includeQueries: true, includeImages: false };
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

async function getProductsByUserIdsInBatch(
  userId: string,
  ignoreProductIds: string[]
): Promise<ProductDetailsModal[]> {
  const endpoint = "/api/v1/products/getProductsByUserId";
  const payload = {
    userId,
    includeQueries: true,
    includeImages: false,
    fetchInBadge: false,
    ignoreProductIds: ignoreProductIds,
  };
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

async function postAskQuery(params: {
  buyerId: string;
  productId: string;
  question: string;
}): Promise<any> {
  const endpoint = "/api/v1/products/postproductquery";
  const includeBearerToken = true;
  const payload = {
    productQuery: {
      buyerId: params.buyerId,
      productId: params.productId,
      queryContent: { question: params.question },
    },
  };
  const response = await postRequest(endpoint, payload, includeBearerToken);
  return response.json();
}

async function postAnswerQuery(params: {
  productId: string;
  queryId: string;
  answer: string;
}): Promise<any> {
  const endpoint = "/api/v1/products/postproductquery";
  const includeBearerToken = true;
  const payload = {
    productQuery: {
      productId: params.productId,
      queryId: params.queryId,
      queryContent: { answer: params.answer },
    },
  };
  const response = await postRequest(endpoint, payload, includeBearerToken);
  return response.json();
}

async function search(
  searchString: string,
  userPreferences: UserPreferences,
): Promise<ProductDetailsModal[]> {
  // @TODO set location before performing search
  const endpoint = "/api/v1/products/search";
  const payload = ProductConvertor.buildSearchPayload(
    searchString,
    userPreferences
  );
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

async function categorySearch(
  categoryString: string,
  userPreferences: UserPreferences
): Promise<ProductDetailsModal[]> {
  const endpoint = "/api/v1/products/category/category-wise";
  const payload = ProductConvertor.buildCategorySearchPayload(categoryString, userPreferences);
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

/**
 * @deprecated LEGACY multipart upload. Product media now flows through the
 * presigned pipeline (see saveProductMedia) and is persisted as records via
 * updateProductMedia. Do not add new callers.
 */
async function postImages(
  imageData: FormData,
  productId: string
): Promise<any> {
  // @TODO: revisit the url design
  const endpoint = `/api/v1/products/${productId}/images`;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, imageData, includeBearerToken);
  const data = await response.json();
  return data;
}

/**
 * One entry of the product-media persist payload. The product-images endpoint
 * takes `thumbnail` where the business-gallery persist (MediaPersistItem in
 * interfaces/MediaObject) takes `section` — deliberately separate types; keep
 * them in sync with their respective backend contracts, not with each other.
 */
export interface ProductMediaPersistItem {
  mediaType: string;
  objectPath: string;
  thumbnail: string | null;
}

/**
 * Persists the product's FULL media list (declarative replace — the sent list
 * IS the product's media afterwards, in the sent order; records omitted here
 * are deleted). Bytes must already be in storage (presigned pipeline); this
 * call only writes the references that become `productMediaResources`.
 */
async function updateProductMedia(
  productId: string,
  items: ProductMediaPersistItem[]
): Promise<void> {
  const endpoint = `/api/v1/product-images/${productId}/update`;
  // putRequest always attaches the bearer token (no opt-out param, unlike
  // getRequest/postRequest) — this endpoint requires auth anyway.
  const includeBearerToken = true;
  const response = await putRequest(endpoint, items, includeBearerToken);
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(
      (data && (data.errorInformation?.[0]?.errorMessage as string)) ||
        "Couldn't save the product media. Please try again."
    );
  }
}

function buildProductImageUrl(productId: string, index: number): string {
  // Single source of truth for the storage convention lives in
  // ProductImageHelper (conventionalProductImageUrl).
  return conventionalProductImageUrl(productId, index);
}

/**
 * @deprecated LEGACY — probes storage with a full GET just to test existence.
 * Product payloads already carry image URLs (media.imageUrlList); render those
 * directly via ProductImageHelper instead. Kept only until the last legacy
 * consumers are migrated. Do not add new callers.
 */
async function getImage(productId: string): Promise<string[]> {
  const imageUrl = buildProductImageUrl(productId, 1);
  try {
    const response = await getPublicAsset(imageUrl);
    if (response && response.ok) {
      return [imageUrl];
    }
    console.warn(`Image not found for product ${productId}`);
    return [];
  } catch (error) {
    console.error(`Error fetching product-image1 for ${productId}:`, error);
    return [];
  }
}

/**
 * @deprecated LEGACY — sequential blob downloads against conventional storage
 * paths, stopping at the first 404. Only valid as a fallback for old products
 * whose payload carries no image URLs (see useGallery / edit flows). Do not
 * add new callers; use payload URLs via ProductImageHelper.
 */
async function getAllImages(productId: string): Promise<Blob[]> {
  const maxImages = 5;
  const blobs: Blob[] = [];

  for (let i = 1; i <= maxImages; i++) {
    const imageUrl = buildProductImageUrl(productId, i);
    try {
      const response = await getPublicAsset(imageUrl);
      if (!response.ok) {
        break;
      }
      blobs.push(await response.blob());
    } catch (error) {
      console.warn(`Image ${i} fetch failed, stopping.`, error);
      break;
    }
  }

  return blobs;
}

async function localFindsSearch(
  userPreferences: UserPreferences,
): Promise<ProductDetailsModal[]> {
  // @TODO set location before performing search
  const endpoint = "/api/v1/products/localfindssearch";
  const payload = ProductConvertor.buildLocalFindsSearchPayload(
    "",
    userPreferences
  );
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}

export const createInstagramReel = async (
  productId: string,
  sellingPrice: number,
  description: string
) => {
  const endpoint = "/api/v1/products/instagramreel";

  const payload = {
    productId,
    sellingPrice,
    description,
  };

  const response = await postRequest(endpoint, payload, true);

  return response;
};

async function deleteProduct(productId: string): Promise<any> {
  // backend API endpoint
  const endpoint = `/api/v1/products/delete-product?pid=${productId}`;

  const includeBearerToken = true;

  // no body → so we use postRequest with empty object
  const response = await postRequest(endpoint, {}, includeBearerToken);

  return response.json();
}

/**
 * Analyze an external listing (e.g., Facebook Marketplace) by URL
 * @param externalUrl - The URL of the external listing to analyze
 * @returns The analyzed listing data
 */
async function analyzeExternalListing(externalUrl: string): Promise<any> {
  // DEBUG: Set to true to use mocked response, false for real API
  const USE_MOCK_RESPONSE = false;

  if (USE_MOCK_RESPONSE) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 7000));
    
    return {
      errorInformation: null,
      generalProcessingStatus: null,
      contents: [
        {
          externalListingSummary: "📦 **iPhone 13 128gb**\n\n💰 **Price:** ₹32,000\n\n✨ **Condition:** Used - like new\n\n📝 **Description**\nVery good condition smartphone with original box and bill included. The iPhone 13 is 3 years old, with no scratches or dents on the body. The battery health stands at 83%, ensuring reliable daily usage.\n\n🔑 **Key Details**\n- Model: iPhone 13\n- Storage: 128 GB\n- Battery health: 83%\n- Age: 3 years\n\n💬 **Seller Notes**\nIncludes the original box and bill for warranty and authenticity verification.",
          imageUrls: [
            "https://scontent-lga3-1.xx.fbcdn.net/v/t39.84726-6/599512032_619882101188866_8294291317533042479_n.jpg",
            "https://scontent-lga3-2.xx.fbcdn.net/v/t45.5328-4/598207563_827127986829231_4067483558429019558_n.jpg"
          ]
        }
      ]
    };
  }

  const endpoint = `/api/v1/products/analyzeexternallisting`;
  
  const payload = {
    url: externalUrl
  };
  
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  
  return response.json();
}

async function getProductsByBusinessId(businessId: string): Promise<any> {
  const endpoint = `/api/v1/products/get-products-by-business-id/${businessId}`;
  const includeBearerToken = true;
  const response = await getRequest(endpoint, includeBearerToken);
  await throwIfNotOk(response);
  return response.json();
}
/** A business's listings, unwrapped from the `{ contents: [{ products }] }` envelope. */
async function getBusinessListingProducts(businessId: string): Promise<ProductDetailsModal[]> {
  const res = await getProductsByBusinessId(businessId);
  return (res?.contents?.[0]?.products ?? []) as ProductDetailsModal[];
}
async function setDisplayableProducts(productIds: string[]): Promise<any> {
  const endpoint = "/api/v1/products/set-displayable-products";
  const payload = { productIds };
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  return response.json();
}

/**
 * Owner-only storefront visibility toggle. `show=true` un-hides, `show=false`
 * hides — maps straight to the `showHide` query param, no request body.
 * Returns the updated product exactly as the backend sent it (same raw,
 * already-camelCase shape every other products/* list endpoint returns — see
 * MyListingsThunk.ts for the same no-converter convention), or `undefined` if
 * the response didn't include it for some reason.
 */
async function setProductVisibility(
  productId: string,
  show: boolean
): Promise<ProductDetailsModal | undefined> {
  const endpoint = `/api/v1/products/${productId}/visibility?showHide=${show}`;
  const includeBearerToken = true;
  const response = await putRequest(endpoint, {}, includeBearerToken);
  await throwIfNotOk(response);
  const data = await response.json().catch(() => null);
  const products = data?.contents?.[0]?.products;
  return Array.isArray(products)
    ? products.find((p: ProductDetailsModal) => p.productId === productId)
    : undefined;
}

/**
 * Owner-only listing-status change (Mark as Sold ⇄ Relist). Status rides as a
 * query param with NO request body — same shape as setProductVisibility's
 * `?showHide=`. Nothing else about the product is sent, so the update can't
 * clobber fields the caller happens not to hold (list payloads carry only a
 * subset). Throws on a non-OK response.
 *
 * Returns the updated product when the response carries one, so the caller can
 * confirm the status actually changed; `undefined` when the response shape
 * gives us nothing to check against.
 */
async function setListingStatus(
  productId: string,
  listingStatus: "SOLD" | "ACTIVE"
): Promise<ProductDetailsModal | undefined> {
  const endpoint = `/api/v1/products/change-listing-status/${productId}?listingStatus=${listingStatus}`;
  const includeBearerToken = true;
  const response = await putRequest(endpoint, {}, includeBearerToken);
  await throwIfNotOk(response);
  const data = await response.json().catch(() => null);
  // Tolerate both envelopes seen on products/* writes: the delete/visibility
  // { contents: [{ products: [...] }] } wrapper, and a bare product object.
  const products = data?.contents?.[0]?.products;
  if (Array.isArray(products)) {
    return products.find((p: ProductDetailsModal) => p.productId === productId);
  }
  const single = data?.product ?? data;
  return single?.productId === productId ? single : undefined;
}

/**
 * Assigns (`sectionId`) or clears (`null`) a product's business-section —
 * PUT /products/{productId}/section. Caller must own the product. Standard
 * product-domain error shape (an unknown/wrong-business sectionId fails with
 * VALIDATION_ERROR in `errors`, same as any other required-field validation
 * on this domain) — `throwIfNotOk` already surfaces that via
 * extractApiErrorMessage, same as every other products/* write in this file.
 * Returns the updated product when the response carries one, same tolerant-
 * envelope handling as setProductVisibility/setListingStatus above.
 */
async function updateProductSection(
  productId: string,
  sectionId: string | null
): Promise<ProductDetailsModal | undefined> {
  const endpoint = `${ServiceEndpoints.PRODUCT_SECTION_BASE}/${productId}/section`;
  const includeBearerToken = true;
  const response = await putRequest(endpoint, { sectionId }, includeBearerToken);
  await throwIfNotOk(response, "Couldn't update the product's section. Please try again.");
  const data = await response.json().catch(() => null);
  const products = data?.contents?.[0]?.products;
  if (Array.isArray(products)) {
    return products.find((p: ProductDetailsModal) => p.productId === productId);
  }
  const single = data?.product ?? data;
  return single?.productId === productId ? single : undefined;
}

export default {
  createProduct,
  bulkCreateProducts,
  deleteProductsByIds,
  deleteProductsByBusinessId,
  getProductsByBusinessId,
  getBusinessListingProducts,
  getProductsByIds,
  getProductDetailsById,
  getProductsByUserIds,
  postAskQuery,
  postAnswerQuery,
  search,
  categorySearch,
  postImages,
  updateProductMedia,
  getImage,
  getAllImages,
  localFindsSearch,
  getProductsByUserIdsInBatch,
  createInstagramReel,
  deleteProduct,
  updateProduct,
  applyProductPatch,
  analyzeExternalListing,
  setDisplayableProducts,
  setProductVisibility,
  setListingStatus,
  updateProductSection,
};
